/*
 * Copyright 2023 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { symbolClientContext as oldSymbolClientContext } from "@osdk/shared.client";
import { symbolClientContext } from "@osdk/shared.client2";
import { PalantirApiError, UnknownError } from "@osdk/shared.net.errors";
export async function foundryPlatformFetch(client, [httpMethodNum, origPath, flags, contentType, responseContentType], ...args) {
  const path = origPath.replace(/\{([^}]+)\}/g, () => encodeURIComponent(args.shift()));
  const body = flags & 1 ? args.shift() : undefined;
  const queryArgs = flags & 2 ? args.shift() : undefined;
  const headerArgs = flags & 4 ? args.shift() : undefined;
  const method = ["GET", "POST", "PUT", "DELETE", "PATCH"][httpMethodNum];
  return await apiFetch(client[symbolClientContext] ?? client[oldSymbolClientContext] ?? client, method, path, body, queryArgs, headerArgs, contentType, responseContentType);
}
async function apiFetch(clientCtx, method, endpointPath, data, queryArguments, headers, requestMediaType, responseMediaType) {
  const url = parseUrl(clientCtx.baseUrl, endpointPath);
  for (const [key, value] of Object.entries(queryArguments || {})) {
    if (value == null) {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        url.searchParams.append(key, item);
      }
    } else {
      url.searchParams.append(key, value);
    }
  }
  const headersInit = new Headers();
  headersInit.set("Content-Type", requestMediaType ?? "application/json");
  headersInit.set("Accept", responseMediaType ?? "application/json");
  Object.entries(headers || {}).forEach(([key, value]) => {
    if (key === "Content-Type" && typeof value === "string") {
      headersInit.set("Content-Type", value);
    } else if (key === "Accept" && typeof value === "string") {
      headersInit.set("Accept", value);
    } else if (value != null) {
      headersInit.append(key, value.toString());
    }
  });
  const body = data == null || data instanceof globalThis.Blob ? data : JSON.stringify(data);
  // Because this uses the client's fetch, there is a 99.99% chance that it is already going
  // to handle the error case and throw a PalantirApiError since its wrapped in a
  // createFetchOrThrow.
  const response = await clientCtx.fetch(url.toString(), {
    body,
    method: method,
    headers: headersInit
  });
  // However, if we ended up using a "regular" fetch, the
  // error status codes are not thrown by fetch automatically,
  // we have to look at the ok property and behave accordingly
  if (!response.ok) {
    try {
      const convertedError = await response.json();
      return new PalantirApiError(convertedError.message, convertedError.errorName, convertedError.errorCode, convertedError.errorDescription, response.status, convertedError.errorInstanceId, convertedError.parameters);
    } catch (e) {
      if (e instanceof Error) {
        return new UnknownError(e.message, "UNKNOWN");
      }
      return new UnknownError("Unable to parse error response", "UNKNOWN");
    }
  }
  // Do not return anything if its a 204. Do not parse either!
  if (response.status === 204) {
    return;
  }
  if (responseMediaType == null || responseMediaType === "application/json") {
    return await response.json();
  }
  return response;
}
export function parseUrl(baseUrl, endpointPath) {
  baseUrl += baseUrl.endsWith("/") ? "" : "/";
  return new URL(`api${endpointPath}`, baseUrl);
}